import * as angular from 'angular';
import * as _ from "lodash";
import {LocationType} from "../../commons/types/location.type";
import {PredefinedSkillType} from "../../skills/types/predefined-skill.type";
import {SplitService} from "../../commons/services/split-service.service";
import {OrderRequestsService} from "./order-requests.service";
import {DeliveriesService} from "../../deliveries/services/deliveries-service.service";
import {BringozLoaderService} from "../../commons/services/bringoz-loader.service";
import {BooleanMap, PodSettingsMap, getMandatoryKey} from "../utils/identifications.util";

export class OrdersService {


  private locationTypes: typeof LocationType = LocationType;

  constructor(private deliveriesService: DeliveriesService, private agentConstants, private orderRequestsService: OrderRequestsService,
              private $log, private bringozLoaderService: BringozLoaderService, private splitService: SplitService) {

  }

  // ******************************
  // External methods
  // ******************************

  getOrderSettings(accountId?, serviceType?, programType?, quoteTypes?, programId?, timeWindowGapDays?, from?: number, zoneId?: string) {
    this.bringozLoaderService.on();
    return this.orderRequestsService.getOrderSettings(accountId, serviceType, programType, quoteTypes, programId, timeWindowGapDays, from, zoneId)
      .then((orderSettings) => {
        return orderSettings;
      })
      .catch((error) => {
        this.$log.error('Failed to get orderSettings', error);
      })
      .finally(() => {
        this.bringozLoaderService.off();
      })
  }

  getOrderQuote(services, accountId, selectedPricePlanType, from, to) {
    this.bringozLoaderService.on();
    return this.orderRequestsService.getOrderQuote(services, accountId, selectedPricePlanType, from, to)
      .then((res) => {
        // return res.services[0].quotes;
        return res.services[0];
      })
      .catch((error) => {
        this.$log.error('Failed to get quotes', error);
      })
      .finally(() => {
        this.bringozLoaderService.off();
      })
  }

  getEmptyDeliveryOrder(account, defaultDepot, program, selectedProgramType?, duplicateService?) {
    const enabledProgramType = _.find(program.entryList, (pt) => {
      return selectedProgramType ? (pt.type === selectedProgramType) : pt.enabled;
    });
    const pricePlanType = enabledProgramType ? enabledProgramType.type : _.get(program.entryList, '[0].type'); //Set default Program Type
    // const duplicateServiceCopy = duplicateService ? angular.copy(duplicateService) : undefined;
    //
    // if (!!duplicateServiceCopy) {
    //   _.unset(duplicateServiceCopy, 'billing');
    //   _.unset(duplicateServiceCopy, 'routeId');
    //   _.unset(duplicateServiceCopy, 'routeReference');
    //   _.unset(duplicateServiceCopy, 'displayId');
    //   _.unset(duplicateServiceCopy, 'proNumber');
    //
    //   // Reset Missing Items
    //   if (_.get(duplicateServiceCopy, 'packageList[0].items')) {
    //     this.resetMissingItems(duplicateServiceCopy.packageList);
    //   }
    //
    //   _.forEach(duplicateServiceCopy.taskList, (t) => {
    //     _.unset(t, 'routeId');
    //   })
    // }

    let duplicateServiceCopy = undefined;

    if (duplicateService) {
      duplicateServiceCopy = this.getDuplicateService(duplicateService);
    }

    return {
      accountId: account.id,
      pricePlanType: _.get(duplicateService, 'pricePlanType', pricePlanType),
      serviceList: [duplicateServiceCopy || this.getEmptyDeliveryService(account, defaultDepot, pricePlanType)]
    };
  }

  getDuplicateService(duplicateService) {
    return {
      serviceType: duplicateService.serviceType,
      requiredSkills: duplicateService.requiredSkills,
      tagNames: duplicateService.tagNames,
      packed: duplicateService.packed,
      pricePlanType: duplicateService.pricePlanType,
      externalId: duplicateService.externalId,
      referenceIds: duplicateService.referenceIds,
      invoiceNumber: duplicateService.invoiceNumber,
      notes: duplicateService.notes,
      vehicleType: duplicateService.vehicleType,
      urgency: duplicateService.urgency,
      packageList: this.resetMissingItems(duplicateService.packageList),
      notBefore: duplicateService.notBefore,
      notAfter: duplicateService.notAfter,
      metaData: duplicateService.metaData,
      taskList: duplicateService.taskList.map(task => {
        return {
          taskType: task.taskType,
          taskValidationList: task.taskValidationList,
          subTasks: task.subTasks,
          relations: task.relations,
          taskSpec: {
            address: task.taskSpec.address,
            contact: task.taskSpec.contact,
            extraInfo: task.taskSpec.extraInfo,
            identifications: task.taskSpec.identifications,
            locationDetails: task.taskSpec.locationDetails,
            notAfter: task.taskSpec.notAfter,
            notBefore: task.taskSpec.notBefore,
            purpose: task.taskSpec.purpose,
            serviceTime: task.taskSpec.serviceTime,
            useProxyCall: task.taskSpec.useProxyCall
          }
        }
      })
    }
  }

  getEmptyOperationOrder(account, defaultDepot, program, defaultProgramType?, service?, isDuplicate?) {
    const enabledProgramType = _.find(program.entryList, (pt) => {
      return defaultProgramType ? (pt.type === defaultProgramType) : pt.enabled;
    });
    const pricePlanType = enabledProgramType ? enabledProgramType.type : _.get(program.entryList, '[0].type'); //Set default Program Type
    // const serviceCopy = service ? angular.copy(service) : undefined;
    //
    // if (!!serviceCopy && !!isDuplicate) {
    //   _.unset(serviceCopy, 'billing');
    //   _.unset(serviceCopy, 'routeId');
    //   _.unset(serviceCopy, 'routeReference');
    //   _.unset(serviceCopy, 'displayId');
    //   _.unset(serviceCopy, 'proNumber');
    //
    //   // Reset Missing Items
    //   if (_.get(serviceCopy, 'packageList[0].items')) {
    //     this.resetMissingItems(serviceCopy.packageList);
    //   }
    //
    //   _.forEach(serviceCopy.taskList, (t) => {
    //     _.unset(t, 'routeId');
    //   })
    // }

    let serviceCopy = (service && !!isDuplicate) ?
      this.getDuplicateService(service) :
      (service ? angular.copy(service) : undefined);

    return {
      accountId: account.id,
      pricePlanType: _.get(serviceCopy, 'pricePlanType', pricePlanType),
      serviceList: [serviceCopy || this.getEmptyOperationalService(account, defaultDepot, pricePlanType)]
    };
  }

  getEmptyDeliveryService(account, defaultDepot, programTypeId, notBefore?, notAfter?) {
    return {
      serviceType: 'Delivery',
      requiredSkills: [PredefinedSkillType.Delivery],
      notBefore: notBefore,
      notAfter: notAfter,
      vehicleType: undefined,
      pricePlanType: programTypeId,
      packageList: [{
        items: []
      }],
      referenceIds: undefined,
      invoiceNumber: undefined,
      externalId: undefined,
      taskList: [{
        taskType: 'Stopover',
        taskSpec: {
          notBefore: notBefore,
          notAfter: notAfter,
          purpose: 'Pickup',
          extraInfo: account?.note,
          address: defaultDepot ? _.get(defaultDepot, 'address', {}) : _.get(account, 'address', {}),
          contact: {
            name: defaultDepot ? defaultDepot.contactName : _.get(account, 'name'),
            businessName: defaultDepot ? defaultDepot.name : _.get(account, 'name'),
            phoneNo: defaultDepot ? defaultDepot.phoneNumber : _.get(account, 'phoneNumber'),
            additionalPhoneNo: defaultDepot ? defaultDepot.additionalPhoneNo : _.get(account, 'additionalPhoneNo')
          },
          identifications: {},
          locationDetails: {
            id: defaultDepot ? defaultDepot.id : _.get(account, 'id'),
            locationType: defaultDepot ? this.locationTypes.DEPOT : this.locationTypes.ACCOUNT
          },
          depot: defaultDepot
        }
      }, {
        taskType: 'Stopover',
        taskSpec: {
          notBefore: notBefore,
          notAfter: notAfter,
          purpose: 'Dropoff',
          address: {},
          contact: {},
          identifications: {},
          locationDetails: {
            locationType: this.locationTypes.CUSTOM
          }
        }
      }]
    }
  }

  getEmptyOperationalService(account, defaultDepot, programTypeId, notBefore?, notAfter?) {
    return {
      serviceType: 'Operation',
      requiredSkills: [PredefinedSkillType.Merchandise],
      notBefore: notBefore,
      notAfter: notAfter,
      pricePlanType: programTypeId,
      vehicleType: undefined,
      packageList: [{
        items: []
      }],
      referenceIds: undefined,
      invoiceNumber: undefined,
      externalId: undefined,
      taskList: [
        {
          taskType: 'Stopover',
          subTasks: [],
          taskSpec: {
            notBefore: notBefore,
            notAfter: notAfter,
            purpose: 'Operation',
            extraInfo: account?.note,
            address: defaultDepot ? _.get(defaultDepot, 'address', {}) : _.get(account, 'address', {}),
            contact: {
              name: defaultDepot ? defaultDepot.contactName : _.get(account, 'name'),
              businessName: defaultDepot ? defaultDepot.name : _.get(account, 'name'),
              phoneNo: defaultDepot ? defaultDepot.phoneNumber : _.get(account, 'phoneNumber'),
              additionalPhoneNo: defaultDepot ? defaultDepot.additionalPhoneNo : _.get(account, 'additionalPhoneNo')
            },
            identifications: {},
            locationDetails: {
              id: defaultDepot ? defaultDepot.id : _.get(account, 'id'),
              locationType: defaultDepot ? this.locationTypes.DEPOT : this.locationTypes.CUSTOM
            },
            depot: defaultDepot
          }
        }
      ]
    }
  }

  swapTasks(delivery) {
    const pickupRef = this.deliveriesService.getPickups(delivery)[0];
    const dropoffRef = this.deliveriesService.getDropoffs(delivery)[0];

    const pickupRefCopy = angular.copy(pickupRef);
    const dropoffRefCopy = angular.copy(dropoffRef);

    pickupRef.id = _.get(dropoffRefCopy, 'id');
    dropoffRef.id = _.get(pickupRefCopy, 'id');

    pickupRef.taskSpec.purpose = _.get(dropoffRefCopy, 'taskSpec.purpose');
    dropoffRef.taskSpec.purpose = _.get(pickupRefCopy, 'taskSpec.purpose');

    pickupRef.taskSpec.serviceTime = _.get(dropoffRefCopy, 'taskSpec.serviceTime');
    dropoffRef.taskSpec.serviceTime = _.get(pickupRefCopy, 'taskSpec.serviceTime');

    const deliveryTaskListCopy = angular.copy(delivery.taskList);
    deliveryTaskListCopy[0] = dropoffRef;
    deliveryTaskListCopy[1] = pickupRef;

    delivery.taskList = deliveryTaskListCopy;

    // const pickupTaskSpecRef = angular.copy(_.get(pickupRef, 'taskSpec'));
    // const dropoffTaskSpecRef = angular.copy(_.get(dropoffRef, 'taskSpec'));
    //
    // if (pickupTaskSpecRef && dropoffTaskSpecRef) {
    //
    //   const tmpContact = _.get(pickupTaskSpecRef, 'contact');
    //   pickupTaskSpecRef.contact = _.get(dropoffTaskSpecRef, 'contact');
    //   dropoffTaskSpecRef.contact = tmpContact;
    //
    //   const tmpDepot = _.get(pickupTaskSpecRef, 'depot');
    //   pickupTaskSpecRef.depot = _.get(dropoffTaskSpecRef, 'depot');
    //   dropoffTaskSpecRef.depot = tmpDepot;
    //
    //   const tmpDepotId = _.get(pickupTaskSpecRef, 'depotReference.id');
    //   pickupTaskSpecRef.depotReference = !!_.get(dropoffTaskSpecRef, 'depotReference.id') ?
    //     {id: dropoffTaskSpecRef.depotReference.id} : undefined;
    //   dropoffTaskSpecRef.depotReference = !!tmpDepotId ?
    //     {id: tmpDepotId} : undefined;
    //
    //   const tmpAddress = _.get(pickupTaskSpecRef, 'address');
    //   pickupTaskSpecRef.address = _.get(dropoffTaskSpecRef, 'address');
    //   dropoffTaskSpecRef.address = tmpAddress;
    //
    //   const tmpExtraInfo = _.get(pickupTaskSpecRef, 'extraInfo');
    //   pickupTaskSpecRef.extraInfo = _.get(dropoffTaskSpecRef, 'extraInfo');
    //   dropoffTaskSpecRef.extraInfo = tmpExtraInfo;
    //
    //   const tmpIdentifications = _.get(pickupTaskSpecRef, 'identifications');
    //   pickupTaskSpecRef.identifications = _.get(dropoffTaskSpecRef, 'identifications');
    //   dropoffTaskSpecRef.identifications = tmpIdentifications;
    //
    //   const tmpRelations = _.get(pickupRef, 'relations');
    //   pickupRef.relations = _.get(dropoffRef, 'relations');
    //   dropoffRef.relations = tmpRelations;
    //
    //   const tmpCustomerId = _.get(pickupTaskSpecRef, 'customerId');
    //   pickupTaskSpecRef.customerId = _.get(dropoffTaskSpecRef, 'customerId');
    //   dropoffTaskSpecRef.customerId = tmpCustomerId;
    //
    //   pickupRef.taskSpec = pickupTaskSpecRef;
    //   dropoffRef.taskSpec = dropoffTaskSpecRef;
    // }
  }

  // resetMissingItems(packageList) {
  //   let totalCount = 0;
  //
  //   packageList[0].items.forEach((item) => {
  //     if (item.missingItems) {
  //       item.missingItems.forEach((m) => {
  //         item.count += m.quantity;
  //       })
  //       item.missingItems = undefined;
  //       totalCount += item.count;
  //     }
  //   })
  //   packageList[0].count = totalCount;
  // }

  // Builds the initial `task.taskSpec.identifications` from the split backend
  // read-shape. The mandatory flag is only set when the POD itself is required,
  // so the persisted state is always self-consistent (no orphan
  // `is{PodKey}FillRequired: true` on an unselected POD).
  buildDefaultIdentifications(
    podSettings: PodSettingsMap,
    fillRequiredSettings: BooleanMap = {},
  ): BooleanMap {
    const result: BooleanMap = {};
    _.forOwn(podSettings, (value, key) => {
      const isRequired = value === 'Required';
      result[key] = isRequired;
      result[getMandatoryKey(key)] = isRequired && !!fillRequiredSettings[key];
    });
    return result;
  }

  // Builds the `options` map for `identifications-multi-select`. POD status
  // is preserved because the component uses `"Required"` to lock required
  // PODs in the dropdown. The `is{PodKey}FillRequired` keys are carried as
  // metadata so the component can default the Mandatory switch when the user
  // toggles an Optional POD on.
  buildIdentificationOptions(
    podSettings: PodSettingsMap,
    fillRequiredSettings: BooleanMap = {},
  ): PodSettingsMap {
    const result: PodSettingsMap = {};
    _.forOwn(podSettings, (value, key) => {
      result[key] = value;
      result[getMandatoryKey(key)] = !!fillRequiredSettings[key];
    });
    return result;
  }

  resetMissingItems(packageList) {
    const packageListCopy = angular.copy(packageList);
    let totalCount = 0;

    packageListCopy[0].items.forEach((item) => {
      if (item.missingItems) {
        item.missingItems.forEach((m) => {
          item.count += m.quantity;
        })
        item.missingItems = undefined;
        totalCount += item.count;
      }
    })
    packageListCopy[0].count = totalCount;

    return packageListCopy;
  }


}

angular
  .module('bringoz.admin.orders')
  .service('ordersService', OrdersService);
